import eligibilityCheckServices from "./eligibilityCheckService";

interface Country {
  countryId: string;
  countryDesc: string;
  active: boolean;
  createdOn: string | null;
  createdBy: string | null;
  modifiedOn: string | null;
  modifiedBy: string | null;
  createdName: string | null;
  modifiedName: string | null;
}
interface MaritalStatus {
  id?: string | number;
  code?: string;
  value?: string;
  label?: string;
  name?: string;
  desc?: string;
}

interface Nationality {
  id?: string | number;
  code?: string;
  value?: string;
  label?: string;
  name?: string;
  desc?: string;
}

interface University {
  universityId: number;
  universityCode: string;
  universityName: string;
  startDate: number;
  expiryDate: number;
  countryId: string;
  universityHostCode: string | null;
  universityRank: string | null;
  active: boolean;
  createdOn: string | null;
  createdBy: string | null;
  modifiedOn: string | null;
  modifiedBy: string | null;
  createdName: string | null;
  modifiedName: string | null;
}

interface Institute {
  instituteId: number;
  instituteCode: string;
  instituteName: string;
  instituteAddress: string | null;
  addressLine1: string;
  universityId: number;
  startDate: number;
  expiryDate: number;
  contactPhoneNo1: string;
  contactPhoneNo2: string;
  contactPersonName: string;
  contactPersonPhoneNo: string;
  contactPersonEmail: string;
  instituteHostCode: string | null;
  rank: string | null;
  active: boolean;
  createdOn: string | null;
  createdBy: string | null;
  modifiedOn: string | null;
  modifiedBy: string | null;
  createdName: string | null;
  modifiedName: string | null;
}

interface Course {
  courseId: number;
  courseCode: string;
  courseName: string;
  courseType: number;
  courseStream: number;
  courseSubStream: number;
  hostCode: string;
  courseDuration: number;
  startDate: number;
  expiryDate: number;
  version: number;
  lastmntBy: number;
  lastmntOn: number;
  recordStatus: string;
  roleCode: string;
  nextRoleCode: string;
  taskId: string;
  nextTaskId: string;
  recordType: string;
  workflowId: number;
  timeOfTheChange: number;
  timeOfCommit: number;
  seqNo: string;
}

type DropdownOption = { value: string; label: string; id?: string | number };

interface DropdownResponse<T> {
  status: string;
  message: string;
  data: T[];
}
interface Salutations {
  salutationCode: string;
  salutationDesc: string;
}
interface UniversitiesRequest {
  universityName?: string;
  countryId: string;
}

interface InstitutesRequest {
  instituteName?: string;
  universityId: string;
  countryId: string;
}

interface CoursesRequest {
  courseName?: string;
  instituteId: any;
}

const dropdownService = {
  getCountries: async (): Promise<DropdownResponse<Country>> => {
    const response = await eligibilityCheckServices.getCountries();
    return response.data;
  },
  getMaritalStatusMaster: async (): Promise<DropdownResponse<MaritalStatus>> => {
    const response = await eligibilityCheckServices.getMaritalStatusMaster();
    return response.data;
  },
    getAddressTypes: async (): Promise<{ value: string; label: string }[]> => {
    try {
      const response = await eligibilityCheckServices.getAddressTypes();
      const data = response?.data?.data || response?.data || [];
      const mapped = (Array.isArray(data) ? data : []).map((item: any) => ({
        value: String(item?.addressTypeCode?? ""),
        label: String(item?.addressTypeDiscription ?? ""),
      })).filter((opt: any) => opt.value && opt.label && opt.value !== "undefined");
      if (mapped.length > 0) return mapped;
    } catch {
      // fall through to dummy data
    }
    return [
      { value: "RESIDENTIAL", label: "Residential" },
    ];
  },

  getPropertyTypes: async (): Promise<DropdownOption[]> => {
    try {
      const response = await eligibilityCheckServices.getPropertyTypes();
      const data = response?.data?.data || response?.data || [];
      const mapped = (Array.isArray(data) ? data : []).map((item: any) => ({
        value: String(item?.code ?? ""),
        label: String(item?.value ?? ""),
        id: item?.serialId,
      })).filter((opt: any) => opt.value && opt.label && opt.value !== "undefined");
      if (mapped.length > 0) return mapped;
    } catch {
      // fall through to dummy data
    }
    return [
      { value: "LAND", label: "Land" },
    ];
  },

  getPropertySubTypes: async (propertyTypeCode?: string): Promise<DropdownOption[]> => {
    try {
      const response = await eligibilityCheckServices.getPropertySubTypes(propertyTypeCode);
      const data = response?.data?.data || response?.data || [];
      const mapped = (Array.isArray(data) ? data : []).map((item: any) => ({
        value: String(item?.code ??  ""),
        label: String(item?.value ?? ""),
        id: item?.serialId,
      })).filter((opt: any) => opt.value && opt.label && opt.value !== "undefined");
      if (mapped.length > 0) return mapped;
    } catch {
      // fall through to dummy data
    }
    // Dummy data - filtered by propertyTypeCode if provided
    const allSubTypes: Record<string, { value: string; label: string }[]> = {
      
    };
    if (propertyTypeCode && allSubTypes[propertyTypeCode]) {
      return allSubTypes[propertyTypeCode];
    }
    return Object.values(allSubTypes).flat();
  },
  getNationalityMaster: async (): Promise<DropdownResponse<Nationality>> => {
    const response = await eligibilityCheckServices.getNationalityMaster();
    return response.data;
  },
  getCitiesByCountryState: async (
    countryName: string,
    stateName?: string
  ): Promise<any> => {
    const response = await eligibilityCheckServices.getCitiesByCountryState(
      countryName,
      stateName
    );
    return response.data;
  },

  getUniversities: async (
    requestData: UniversitiesRequest
  ): Promise<DropdownResponse<University>> => {
    const response = await eligibilityCheckServices.getUniversities(
      requestData
    );
    return response.data;
  },

  getInstitutes: async (
    requestData: InstitutesRequest
  ): Promise<DropdownResponse<Institute>> => {
    const response = await eligibilityCheckServices.getInstitutes(requestData);
    return response.data;
  },

  getCourses: async (
    requestData: CoursesRequest
  ): Promise<DropdownResponse<Course>> => {
    const response = await eligibilityCheckServices.getCourses(requestData);
    return response.data;
  },

  getMasters: async (): Promise<any> => {
    const response = await eligibilityCheckServices.getMasters();
    return response.data;
  },

  getProducts: async (): Promise<{
    status: string;
    message: string;
    data: { code: string; name: string }[];
  }> => {
    const response = await eligibilityCheckServices.getProducts();
    return response.data;
  },
  getSalutations: async (): Promise<DropdownResponse<Salutations>> => {
    const response = await eligibilityCheckServices.getSalutations();
    return response.data;
  },

  getJourneyPennantRelationshipMaster: async (): Promise<any> => {
    const response = await eligibilityCheckServices.getJourneyPennantRelationshipMaster();
    return response.data;
  },
};

export default dropdownService;
